import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
// @mui
import { alpha, useTheme, Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Unstable_Grid2";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";

// --- LIGHTBOX IMPORTS ---
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

// components
import Label from "src/components/label";
import Iconify from "src/components/iconify";
import Image from "src/components/image";
import useCaySamFilterOptions from "src/utils/use-caysam-filter-options";
import { useResponsive } from "src/hooks/use-responsive";
import { useCaySamNFT } from "../hooks/caysam";

// ----------------------------------------------------------------------

type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface InfoItemProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  color?: ColorSchema;
}

function InfoItem({ icon, label, value, color = "primary" }: InfoItemProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme: Theme) => alpha((theme.palette as any)[color].main, 0.12),
          color: (theme: Theme) => (theme.palette as any)[color].main,
        }}
      >
        <Iconify icon={icon} width={24} />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function CaySamNFTView() {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { loading, caySam, getCaySam } = useCaySamNFT();
  const { getTrangThaiText, getTuoiText } = useCaySamFilterOptions();
  const isMobile = useResponsive("down", "md");

  const [activeImage, setActiveImage] = useState<string | null | undefined>(null);
  const [openLightbox, setOpenLightbox] = useState(false);
  
  useEffect(() => {
    if (id) {
      getCaySam(id);
    }
  }, [id, getCaySam]);

  const sortedDiary = useMemo(() => caySam?.caySamNhatKys
      ? [...caySam.caySamNhatKys].sort(
          (a, b) => new Date(b?.ngayGhi || 0).getTime() - new Date(a?.ngayGhi || 0).getTime()
        )
      : [], [caySam]);

  const lightboxSlides = useMemo(() => 
     sortedDiary
      .map((item) => item?.hinhAnhTongQuan) 
      .filter((src): src is string => typeof src === 'string' && src !== "") 
      .map((src) => ({ src })) 
  , [sortedDiary]);

  useEffect(() => {
    if (sortedDiary && sortedDiary.length > 0) {
      setActiveImage(sortedDiary[0]?.hinhAnhTongQuan);
    }
  }, [sortedDiary]);


  if (loading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!caySam) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Iconify icon="solar:leaf-bold-duotone" width={80} sx={{ color: "text.disabled", mb: 2 }} />
        <Typography variant="h5" color="text.secondary">
          Không tìm thấy dữ liệu cây sâm
        </Typography>
      </Container>
    );
  }

  const latestStatus = sortedDiary[0];

  const renderHero = (
    <Grid container spacing={3}>
      <Grid xs={12} md={7} lg={8}>
        <Card sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box 
            sx={{ 
                position: "relative", 
                borderRadius: 2, 
                overflow: "hidden", 
                flexGrow: 1, 
                bgcolor: 'background.neutral',
                cursor: 'zoom-in',
                '&:hover .overlay': { opacity: 1 }
            }}
            onClick={() => setOpenLightbox(true)}
          >
            {caySam.isNFC && (
              <Label
                variant="filled"
                color="info"
                startIcon={<Iconify icon="mdi:nfc" />}
                sx={{ position: "absolute", top: 16, right: 16, zIndex: 9, textTransform: "uppercase" }}
              >
                NFC Verified
              </Label>
            )}
            
            <Image
              src={activeImage || caySam.caySamNhatKys?.[0]?.hinhAnhTongQuan || ""}
              ratio="4/3"
              alt={caySam.maCaySam}
              sx={{ borderRadius: 1.5, height: '100%', objectFit: 'cover' }}
            />

            <Box className="overlay" sx={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                bgcolor: alpha(theme.palette.grey[900], 0.3),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 0.2s',
            }}>
                 <Iconify icon="solar:maximize-bold" width={48} sx={{ color: 'white' }} />
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2, overflowX: 'auto', py: 1, px: 0.5 }}>
            {sortedDiary.slice(0, 6).map((log, index) => {
               if(!log?.hinhAnhTongQuan) return null;
               
               const isSelected = activeImage === log?.hinhAnhTongQuan;
               return (
                <Box
                  key={log?.id || index}
                  onClick={() => setActiveImage(log?.hinhAnhTongQuan)}
                  sx={{
                    width: 80,
                    height: 80,
                    minWidth: 80,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                    padding: '2px',
                    opacity: isSelected ? 1 : 0.6,
                    transition: theme.transitions.create(['opacity', 'border', 'transform']),
                    '&:hover': { opacity: 1, transform: 'scale(1.05)' }
                  }}
                >
                  <Image src={log?.hinhAnhTongQuan} ratio="1/1" sx={{ borderRadius: 1 }} />
                </Box>
               )
            })}
          </Stack>
        </Card>
      </Grid>

      <Grid xs={12} md={5} lg={4}>
        <Stack spacing={3} sx={{ height: '100%' }}>
          <Card sx={{ p: 3, flexGrow: 1 }}>
             <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="overline" color="text.disabled">Sâm Ngọc Linh</Typography>
                    <Typography variant="h3" sx={{ mb: 1 }}>{caySam.maCaySam}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        <Label color="primary" variant="soft">{getTuoiText(caySam.tuoiCay_ID)}</Label>
                        <Label color="warning" variant="soft">Lô: {caySam.loSam_ID}</Label>
                        <Label color="default" variant="soft">Vị trí: {caySam.viTriTrongLo}</Label>
                    </Stack>
                </Box>
             </Stack>

             <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

             <Grid container spacing={2}>
                 <Grid xs={12}>
                    <InfoItem 
                        icon="solar:health-bold" 
                        label="Sức khỏe hiện tại" 
                        value={
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={(latestStatus?.diemSucKhoe || 0) * 10} 
                                        color={latestStatus?.diemSucKhoe && latestStatus.diemSucKhoe >= 4 ? "success" : "warning"}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                                <Typography variant="h6">{latestStatus?.diemSucKhoe || 0}/5</Typography>
                            </Stack>
                        } 
                        color="error" 
                    />
                 </Grid>
                 <Grid xs={12}>
                    <InfoItem 
                        icon="solar:leaf-bold" 
                        label="Số lá hiện tại" 
                        value={`${latestStatus?.soLa || 0} lá`} 
                        color="success" 
                    />
                 </Grid>
                 <Grid xs={12}>
                     {caySam.blockChain && (
                        <Alert severity="info" variant="outlined" icon={<Iconify icon="icon-park-outline:blockchain" />}>
                           <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block' }}>
                                <b>Blockchain Hash:</b><br/> {caySam.blockChain}
                           </Typography>
                        </Alert>
                     )}
                 </Grid>
             </Grid>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );

  const renderTimeline = (
    <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h5">Nhật ký sinh trưởng</Typography>
            <Label color="info" variant="filled">{sortedDiary.length} bản ghi</Label>
        </Stack>

        <Card>
            <CardContent>
            {sortedDiary.length > 0 ? (
                <Timeline position={isMobile ? "right" : "alternate"} sx={{ p: 0 }}>
                    {sortedDiary.map((item, index) => {
                        const dateObj = new Date(item?.ngayGhi || 0);
                        
                        return (
                            <TimelineItem key={item?.id || index}>
                                <TimelineOppositeContent sx={{ m: 'auto 0', px: 2 }}>
                                    <Typography variant="subtitle2" color="text.primary">
                                        {dateObj.toLocaleDateString("vi-VN")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {dateObj.toLocaleTimeString("vi-VN", { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </TimelineOppositeContent>

                                <TimelineSeparator>
                                    <TimelineConnector sx={{ bgcolor: 'success.lighter' }} />
                                    <TimelineDot 
                                        color="success" 
                                        variant={index === 0 ? 'filled' : 'outlined'}
                                        sx={{ p: 1 }}
                                    >
                                        <Iconify icon="solar:leaf-bold" width={16} />
                                    </TimelineDot>
                                    <TimelineConnector sx={{ bgcolor: 'success.lighter' }} />
                                </TimelineSeparator>

                                <TimelineContent sx={{ py: '12px', px: 2 }}>
                                    <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                                        <CardHeader 
                                            title={
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="subtitle1">Kiểm tra định kỳ</Typography>
                                                    <Chip 
                                                        size="small" 
                                                        label={getTrangThaiText(item?.tinhTrang)} 
                                                        color={item?.tinhTrang === 1 ? "success" : "default"} 
                                                        variant="soft"
                                                    />
                                                </Stack>
                                            }
                                            sx={{ pb: 1 }}
                                        />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <CardContent>
                                            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="caption" color="text.secondary">Số lá</Typography>
                                                    <Typography variant="body2" fontWeight="bold">+{item?.soLa}</Typography>
                                                </Stack>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="caption" color="text.secondary">Sức khỏe</Typography>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                                        {item?.diemSucKhoe}/5
                                                    </Typography>
                                                </Stack>
                                            </Stack>
  
                                            {item?.ghiChu && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                    {item?.ghiChu}
                                                </Typography>
                                            )}

                                            <Stack direction="row" spacing={1}>
                                                {[item?.hinhAnhTongQuan, item?.hinhAnhChiTiet].filter(Boolean).map((img, i) => (
                                                     <Box 
                                                        key={i} 
                                                        sx={{ 
                                                            width: 60, height: 60, 
                                                            borderRadius: 1, 
                                                            overflow: 'hidden', 
                                                            cursor: 'pointer', 
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            '&:hover': { opacity: 0.8 } 
                                                        }}
                                                        onClick={() => {
                                                            if (img) {
                                                                setActiveImage(img);
                                                                setOpenLightbox(true);
                                                            }
                                                        }}
                                                     >
                                                         <Image src={img || ""} ratio="1/1" />
                                                     </Box>
                                                ))}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    })}
                </Timeline>
            ) : (
                <Stack alignItems="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">Chưa có dữ liệu nhật ký.</Typography>
                </Stack>
            )}
            </CardContent>
        </Card>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "background.neutral", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          {renderHero}
          {renderTimeline}
        </Stack>
      </Container>

      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={lightboxSlides}
        plugins={[Zoom]}
        zoom={{
            maxZoomPixelRatio: 3,
            scrollToZoom: true,
        }}
        index={lightboxSlides.findIndex(s => s.src === activeImage) !== -1 
            ? lightboxSlides.findIndex(s => s.src === activeImage) 
            : 0
        }
      />
    </Box>
  );
}